import { useState, useEffect, useRef, useCallback } from 'react';
import { useNotifications } from '@medprompt/shared';
import MicrophoneCheckService from '../../services/MicrophoneCheckService';

// Read by the consultation detail page when it starts capture, then cleared
const PENDING_MIC_DEVICE_KEY = 'scribe-pending-mic-device';

/**
 * Starting a consultation from the scribe list: a live recording (opened on the detail page once
 * the server reports it RECORDING) or an uploaded audio file (which stays on the list).
 */
const useScribeRecordingStart = ({ consultationService, user, navigate }) => {
  const { addNotification, addConsultationUpdateCallback } = useNotifications();
  const [newConsultationModalOpen, setNewConsultationModalOpen] = useState(false);
  const [pendingConsultationUserId, setPendingConsultationUserId] = useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const isUploadFlowRef = useRef(false);
  const userId = user?.userId;

  // Registered once and dispatched through a ref: depending on the handler itself would
  // unsubscribe and resubscribe every time a recording starts or stops.
  const handleRecordingStartedRef = useRef(null);
  useEffect(() => {
    handleRecordingStartedRef.current = (consultationId, statusTransition) => {
      if (
        statusTransition?.from !== 'NON_EXISTENT' ||
        statusTransition?.to !== 'RECORDING' ||
        pendingConsultationUserId !== userId
      ) {
        return;
      }
      setPendingConsultationUserId(null);

      // Only navigate to detail view for live recordings, not uploads
      if (!isUploadFlowRef.current) {
        navigate(`/consultation-detail/${consultationId}`);
      }

      isUploadFlowRef.current = false;
    };
  }, [pendingConsultationUserId, userId, navigate]);

  useEffect(() => {
    if (!addConsultationUpdateCallback) return undefined;
    return addConsultationUpdateCallback(
      (...args) => handleRecordingStartedRef.current?.(...args));
  }, [addConsultationUpdateCallback]);

  const handleStartRecording = useCallback(async (nhsId, templateId, notesMode, speakersCount, encounterType, deviceId) => {
    try {
      setPendingConsultationUserId(userId);
      if (deviceId) {
        MicrophoneCheckService.setLastDeviceId(deviceId);
        try {
          sessionStorage.setItem(PENDING_MIC_DEVICE_KEY, deviceId);
        } catch {
          // ignore
        }
      }
      await consultationService.startRecording(nhsId, templateId, notesMode, speakersCount, encounterType);
      setNewConsultationModalOpen(false);
    } catch (error) {
      setPendingConsultationUserId(null);
      if (error.status === 400) {
        addNotification('scribe-invalid-nhs', 'Invalid NHS Number. Please check and try again.', { isError: true });
      } else {
        addNotification('scribe-start-error', 'Failed to start recording', { isError: true });
      }
      throw error;
    }
  }, [userId, consultationService, addNotification]);

  const handleNewRecordingClick = useCallback(() => {
    // Full mic check + device picker live in NewConsultationModal.
    // Only block obviously unsupported browsers here.
    if (!MicrophoneCheckService.isMediaDevicesSupported()) {
      addNotification(
        'mic-check-failed',
        'Your browser does not support audio recording. Please use a modern browser like Chrome, Firefox, or Edge.',
        { isError: true }
      );
      return;
    }
    setNewConsultationModalOpen(true);
  }, [addNotification]);

  const handleFileUpload = useCallback((event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('audio/')) {
      addNotification('invalid-file', 'Please select an audio file', { isError: true });
      return;
    }

    setUploadingFile(file);
    setUploadModalOpen(true);
    event.target.value = '';
  }, [addNotification]);

  useEffect(() => {
    const handleDragOver = (event) => {
      event.preventDefault();
      setIsDragging(true);
    };

    const handleDragLeave = (event) => {
      if (event.target === event.currentTarget) {
        setIsDragging(false);
      }
    };

    const handleDrop = (event) => {
      event.preventDefault();
      setIsDragging(false);

      const files = Array.from(event.dataTransfer.files || []);
      const audioFile = files.find(f => f.type.startsWith('audio/'));

      if (audioFile) {
        setUploadingFile(audioFile);
        setUploadModalOpen(true);
      } else {
        addNotification('invalid-file', 'Please drop an audio file', { isError: true });
      }
    };

    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('dragleave', handleDragLeave);
    window.addEventListener('drop', handleDrop);

    return () => {
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('dragleave', handleDragLeave);
      window.removeEventListener('drop', handleDrop);
    };
  }, [addNotification]);

  const handleUploadSubmit = useCallback(async (nhsId, templateId, notesMode, speakersCount, encounterType) => {
    if (!uploadingFile) return;

    try {
      isUploadFlowRef.current = true;
      setPendingConsultationUserId(userId);
      await consultationService.uploadConsultationRecording(
        nhsId,
        templateId,
        notesMode,
        speakersCount,
        encounterType,
        uploadingFile
      );
      setUploadModalOpen(false);
      setUploadingFile(null);
      addNotification('upload-success', 'Recording uploaded successfully', { isError: false });
    } catch (error) {
      setPendingConsultationUserId(null);
      isUploadFlowRef.current = false;
      if (error.status === 400) {
        addNotification('upload-error', 'Invalid NHS Number or file', { isError: true });
      } else {
        addNotification('upload-error', 'Failed to upload recording', { isError: true });
      }
      throw error;
    }
  }, [uploadingFile, userId, consultationService, addNotification]);

  const closeNewConsultationModal = useCallback(() => setNewConsultationModalOpen(false), []);
  const closeUploadModal = useCallback(() => {
    setUploadModalOpen(false);
    setUploadingFile(null);
  }, []);

  return {
    isStarting: pendingConsultationUserId !== null,
    isDragging,
    newConsultationModalOpen,
    handleNewRecordingClick,
    closeNewConsultationModal,
    handleStartRecording,
    handleFileUpload,
    uploadModalOpen,
    uploadingFileName: uploadingFile?.name,
    closeUploadModal,
    handleUploadSubmit,
  };
};

export default useScribeRecordingStart;
